/**
 * Códigos de erro de negócio devolvidos em `{codigo, mensagem}`. O HTTP status
 * fica no controller; o código é o que o front usa para decidir o que mostrar.
 */
export const CODIGOS_ERRO = {
  credenciaisInvalidas: 'credenciais_invalidas',
  totpInvalido: 'totp_invalido',
  desafioExpirado: 'desafio_expirado',
  totpJaAtivo: 'totp_ja_ativo',
  codigoInvalido: 'codigo_invalido',
  familiaRevogada: 'familia_revogada',
  /**
   * A conta entrou em outro aparelho e este perfil permite um acesso por vez.
   *
   * Código próprio, e não `familia_revogada`: a mensagem genérica de segurança
   * mandaria a pessoa caçar roubo de cookie onde houve troca de aparelho — e a
   * frase explícita é o que desencoraja senha compartilhada.
   */
  sessaoSubstituida: 'sessao_substituida',
  /** Link de redefinição ausente, vencido ou já usado — uma resposta só. */
  redefinicaoInvalida: 'redefinicao_invalida',
  /** Conversa do editor: o painel so observa, nao comanda o fim dela. */
  sessaoObservada: 'sessao_observada',
  /** A mestre ocupa a vaga unica: some-la da lista esconderia isso. */
  mestreNaoArquiva: 'mestre_nao_arquiva',
  /** Nao ha mestre vigente para recomecar. */
  mestreNaoExiste: 'mestre_nao_existe',
  /** Duas renovacoes ao mesmo tempo: quem perdeu tenta de novo, e so. */
  renovacaoConcorrente: 'renovacao_concorrente',
  emailEmUso: 'email_em_uso',
  ultimoAdmin: 'ultimo_admin',
  maquinaOnline: 'maquina_online',
  capacidadeEsgotada: 'capacidade_esgotada',
  vpsSemMemoria: 'vps_sem_memoria',
  cwdForaDaRaiz: 'cwd_fora_da_raiz',
  fableSemConfirmacao: 'fable_sem_confirmacao',
  sessaoEncerrada: 'sessao_encerrada',
  pedidoExpirado: 'pedido_expirado',
  pedidoJaDecidido: 'pedido_ja_decidido',
  tokenExpirado: 'token_expirado',
  objetoDivergente: 'objeto_divergente',
  sigilosaSemProvedor: 'sigilosa_sem_provedor',
  gravacaoFinalizada: 'gravacao_finalizada',
  gravacaoNaoConcluida: 'gravacao_nao_concluida',
  semReproducao: 'sem_reproducao',
  trabalhoConcluido: 'trabalho_concluido',
  vpsOffline: 'vps_offline',
  tamanhoExcedido: 'tamanho_excedido',
  foraDaRaiz: 'fora_da_raiz',
  conflitoDeConteudo: 'conflito_de_conteudo',
  discoInsuficiente: 'disco_insuficiente',
  /**
   * O disco do ARMAZENAMENTO (o da VPS, compartilhado com a produção de
   * terceiros) não tem espaço para o anexo sem invadir a reserva. Diferente de
   * `disco_insuficiente`, que fala da máquina da sessão.
   */
  armazenamentoCheio: 'armazenamento_cheio',
  /**
   * Entrega em segundo plano cujo DESFECHO não se sabe: o prazo do ack venceu,
   * ou a máquina desconectou no meio do download. O daemon pode ter entregado
   * mesmo assim (ele segue baixando e responde a um socket que já não existe),
   * então a tela NUNCA oferece "tentar de novo" — manda conferir a conversa.
   */
  entregaSemConfirmacao: 'entrega_sem_confirmacao',
  /**
   * O anexo passa de 5 MiB e a máquina da conversa roda um daemon anterior ao
   * que aceita anexo grande: ele só recusaria DEPOIS do upload inteiro.
   */
  maquinaNaoAceitaAnexoGrande: 'maquina_nao_aceita_anexo_grande',
  /** Anexo de outra sessão, ou que nunca chegou ao armazenamento. */
  anexoInvalido: 'anexo_invalido',
  /** A sessão não está viva na máquina: sem SDK, sem capacidades. */
  semCapacidades: 'sem_capacidades',
  /** A resposta não cabe no tipo do pedido (ex.: responder um plano). */
  respostaIncompativel: 'resposta_incompativel',
  /**
   * O modo não existe NAQUELE motor — `plan` e `dontAsk` no Codex.
   *
   * Erro próprio, e não `modo_perigoso_sem_confirmacao`: pedir confirmação
   * convidaria a pessoa a confirmar uma coisa que não vai acontecer de jeito
   * nenhum. Devolvido por `podeUsarModo`, que confere existência ANTES de
   * permissão — `plan` não é perigoso, então com a ordem trocada ele passaria.
   */
  modoIndisponivelNoMotor: 'modo_indisponivel_no_motor',
  /**
   * O modo existe no motor, mas não NAQUELA máquina: `bypassPermissions` numa
   * sessão CODEX da VPS.
   *
   * Erro próprio, e não `sem_permissao`: não falta papel a quem pede — nem
   * ADMIN com confirmação consegue, porque `bypassPermissions` no Codex é
   * `danger-full-access`, e o sandbox é a única barreira daquele motor na
   * máquina que roda produção de terceiros (`modoDisponivelNaMaquina`).
   */
  modoIndisponivelNaVps: 'modo_indisponivel_na_vps',
  /**
   * A mestre foi pedida JÁ em bypass, e o bypass total não vale naquela
   * máquina (ADR-0023: a chave da API e a do daemon, as duas). Erro próprio,
   * e não um nascimento calado em `auto`: quem pediu bypass leria uma mestre
   * que pergunta e não saberia por quê.
   */
  bypassTotalDesligado: 'bypass_total_desligado',
  /**
   * O anexo mora só na máquina da conversa (o cache da VPS expirou, D54) e ela
   * está desligada. Erro próprio, e não 404: o arquivo EXISTE — a tela diz
   * "indisponível: a máquina está desligada", nunca um botão que dá 404.
   */
  anexoNaMaquinaDesligada: 'anexo_na_maquina_desligada',
  /**
   * A máquina não hospeda sessão do Codex — e a mensagem CITA o motivo da sonda.
   *
   * Erro próprio, e não `vps_offline` nem `capacidade_esgotada`: a máquina está
   * no ar e com vaga; o que falta é o motor. Os dois outros mandariam a pessoa
   * investigar rede ou esperar vaga, e nenhuma das duas coisas resolveria.
   *
   * O motivo vem da SONDA (`codex`/`mestreCodex`, D25) e atravessa inteiro até
   * a tela: "está desligado nesta máquina" e "está ligado e não responde: SPAWN
   * ENOENT" pedem gestos diferentes do dono, e uma mensagem genérica apagaria
   * justamente a diferença.
   */
  codexIndisponivel: 'codex_indisponivel',
  /**
   * CODEX sem modelo. O catálogo dele vem da MÁQUINA (`model/list`), e não há
   * padrão da casa — cair no `MODELO_PADRAO` mandaria um nome da Anthropic
   * para o app-server da OpenAI. O código é o mesmo que o `superRefine` do
   * `CriarSessaoSchema` já devolve, para a tela ter UM caminho de mensagem.
   */
  modeloObrigatorioNoMotor: 'modelo_obrigatorio_no_motor',
  /** dontAsk/bypassPermissions sem a confirmação explícita (D37). */
  modoPerigosoSemConfirmacao: 'modo_perigoso_sem_confirmacao',
  /**
   * A sessão do painel dormiu: não há subprocesso para receber o comando.
   *
   * Erro próprio, e não `vps_offline`: a máquina está no ar e responde — quem
   * não existe mais é o processo daquela conversa. Dizer "a máquina não
   * respondeu" mandava investigar rede.
   */
  sessaoDormindo: 'sessao_dormindo',
  /**
   * Adormecer recusado porque o modelo está trabalhando agora.
   *
   * Erro próprio, e não `vps_offline`: a máquina está ótima, e a recusa é
   * deliberada — dormir fecha a fila do SDK, e fechá-la no meio de um turno
   * descarta o que estava em voo. O caminho é esperar ou interromper antes.
   */
  sessaoEmTurno: 'sessao_em_turno',
  /**
   * Encerrar recusado porque há pergunta ou plano esperando decisão.
   *
   * Matar a sessão ali joga fora uma decisão que alguém ainda vai tomar — e o
   * pedido some sem nunca ter sido respondido. Vale só para a mestre: quem
   * clica em Encerrar no painel está com a conversa (e o cartão) na frente.
   */
  sessaoComPendencia: 'sessao_com_pendencia',
  /** "Continuar aqui" pedido numa sessão que já é do painel. */
  sessaoNaoObservada: 'sessao_nao_observada',
  /**
   * A máquina não oferece continuar do painel.
   *
   * Erro próprio, e não `vps_offline`: a máquina respondeu ótimo — o que falta
   * é a chave `CONTINUAR_AQUI` do daemon. Dizer "a máquina não respondeu"
   * mandaria quem lê investigar rede.
   */
  continuarDesligado: 'continuar_desligado',
  /** Já existe uma sessão mestre vigente (F13); encerre-a antes de criar outra. */
  mestreJaExiste: 'mestre_ja_existe',
  /** A máquina não anuncia a capacidade `mestre` na sonda (flag desligada ou daemon antigo). */
  mestreIndisponivel: 'mestre_indisponivel',
  /** O mestre nasce em modo `auto`; criar exige a confirmação explícita (molde do Fable). */
  mestreSemConfirmacao: 'mestre_sem_confirmacao',
  /** O usuário que criou o mestre não existe mais ou foi desativado. */
  mestreSemDono: 'mestre_sem_dono',
  /** Despacho do mestre para o próprio mestre: laço, recusado por construção. */
  alvoEhOMestre: 'alvo_e_o_mestre',
  /**
   * A mestre agindo sobre a OUTRA mestre: trocar o modo, encerrar, adormecer ou
   * decidir pendência dela. Com duas mestres, a recusa a si mesma virava uma
   * volta de dois saltos (revisão do PR 58, 2.1.4).
   */
  alvoEhUmaMestre: 'alvo_e_uma_mestre',
  /** Pedido de FERRAMENTA (comando, edição, chamada MCP) é do dono: a mestre não aprova nem nega. */
  pedidoEhDoDono: 'pedido_e_do_dono',
  /**
   * A mestre pediu, pelo canal, sessão de um motor que não é o dela nem o
   * OpenCode (ADR-0022, D4). A designação continua valendo — a Claude cuida
   * das Claude, a Codex das Codex —, e sessão do outro motor se pede à mestre
   * dele (`listar_mestres` acha o id). A D4 abriu o OpenCode, e só ele.
   */
  motorForaDaDesignacao: 'motor_fora_da_designacao',
  /**
   * A mestre mandou `modelo` ou `esforco` ao criar sessão OPENCODE pelo canal.
   * Na D4 o modelo dela é SEMPRE o padrão da config: aceitar o pedido daria
   * uma segunda resposta para "qual modelo o OpenCode usa", e ignorá-lo calado
   * criaria uma sessão diferente da que a mestre acha que criou.
   */
  modeloDoOpencodeVemDaConfig: 'modelo_do_opencode_vem_da_config',
  /** A resposta de consulta do mestre estourou o teto mesmo reduzida; refine a consulta. */
  respostaGrande: 'resposta_grande',
  /**
   * Flag de leitura da mestre numa conta LEITOR (ADR-0019). Recusada na
   * ESCRITA além de ignorada na leitura: gravar uma flag que nunca vale
   * deixaria a tela dizendo "vê" sobre uma conta que não vê.
   */
  flagDeMestreExigeOperador: 'flag_de_mestre_exige_operador',
  /**
   * A lista de marcação citou uma sessão que não existe. Recusar, nunca
   * ignorar calado: um id fantasma engolido viraria "marquei e não vê".
   */
  sessaoDesconhecida: 'sessao_desconhecida',
  /**
   * A lista de projetos citou um par (máquina, cwd normalizado) que o servidor
   * não publicou (ADR-0020). Mesma disciplina do de cima, e aqui dói mais: a
   * chave do projeto é NORMALIZADA pelo servidor, então um cliente que a
   * montasse sozinho gravaria um grant que não casa com sessão nenhuma — para
   * sempre, sem erro, com a caixa marcada na tela.
   */
  projetoDesconhecido: 'projeto_desconhecido',
  /** A lista de exceções da sessão citou uma conta que não existe (ADR-0020). */
  usuarioDesconhecido: 'usuario_desconhecido',
  /**
   * Exceção de visibilidade sobre uma conta ADMIN (ADR-0020). Recusada porque
   * `veASessao` ignora a exceção para ADMIN (degrau 1 da precedência):
   * gravá-la faria a tela prometer um recorte que o servidor não aplica.
   */
  excecaoNaoValeParaAdmin: 'excecao_nao_vale_para_admin',
  /**
   * Gesto pedido sobre conta DESATIVADA (hoje: disparar redefinição de senha).
   * Mandar o link seria prometer um caminho que o login recusa no fim.
   */
  contaDesativada: 'conta_desativada',
  /**
   * Trocar de motor para o motor em que a sessão JÁ está: não há o que trocar.
   *
   * Erro próprio, e não sucesso silencioso: quem pediu acha que uma sessão
   * nova nasceu no outro motor — e seguir adiante adormeceria e arquivaria a
   * conversa para criar uma cópia dela no MESMO lugar. Retomar e continuar já
   * cobrem seguir a conversa onde ela está.
   */
  mesmoMotor: 'mesmo_motor',
  /**
   * A mestre não troca de motor. A identidade dela é POR MOTOR (o índice
   * parcial `(funcao, motor)` garante uma vigente de cada), e o gesto dela já
   * existe: criar a mestre do outro motor, ou `mestre/recomecar`. Além disso,
   * o transcript dela — que só sai para ADMIN — viraria `primeiraMensagem` de
   * uma sessão comum, visível a quem o escopo nega.
   */
  mestreNaoTrocaMotor: 'mestre_nao_troca_motor',

  /* ------------------------------------------------------------------------ */
  /* O terceiro motor: OpenCode (F16, ADR-0022)                                */
  /* ------------------------------------------------------------------------ */

  /**
   * Sessão comum OPENCODE pedida na VPS (D1, Interface congelada item 4). Lá
   * só mora a mestre: cada processo ocioso come ~400 MB de uma RAM que é do
   * vizinho, e não há sandbox por baixo. Devolvido por
   * `motorDisponivelNaMaquina`, na API e no boot do daemon.
   */
  opencodeSoMestreNaVps: 'opencode_so_mestre_na_vps',
  /**
   * Criar, criar mestre ou trocar para o OPENCODE sem a confirmação explícita
   * (D10). Irmão do `fable_sem_confirmacao`, e pela mesma família de motivo: a
   * conversa sai da rede de casa e a sessão gasta crédito.
   */
  confirmacaoOpencodeObrigatoria: 'confirmacao_opencode_obrigatoria',
  /**
   * Trocar o modo da mestre do OpenCode. Ela nasce em `bypassPermissions` e não
   * sai dele (D3): a trava dela é a deny-list da camada gerenciada, e um modo
   * que pedisse cartão a deixaria parada esperando quem não está olhando.
   */
  modoFixoDaMestreOpencode: 'modo_fixo_da_mestre_opencode',
  /**
   * Modelo ou esforço mandados na criação da mestre do OpenCode. Os dois moram
   * na tela de Configurações (D2, D17): aceitar aqui daria duas respostas para
   * "qual modelo a mestre usa".
   */
  modeloDaMestreVemDaConfig: 'modelo_da_mestre_vem_da_config',
  /**
   * A máquina não hospeda sessão do OpenCode — a irmã do `codex_indisponivel`,
   * e a mensagem CITA o motivo da sonda (`opencode`/`mestreOpencode`): "está
   * desligado nesta máquina", "a camada gerenciada divergiu" e "a chave não
   * chegou" pedem gestos diferentes do dono.
   */
  opencodeIndisponivel: 'opencode_indisponivel',
  /**
   * A frota não tem chave do OpenRouter cadastrada (D5). Erro próprio, e não
   * `opencode_indisponivel`: a máquina está pronta, e quem resolve é o ADMIN na
   * tela de Configurações — não o dono da máquina.
   */
  opencodeSemChave: 'opencode_sem_chave',
  /**
   * A frota TEM a chave, a máquina tem o OpenCode instalado (capacidade
   * `aprovada`), e a chave ainda não está aplicada NELA — o retrato do pulso
   * (`maquinas.opencode.chave.estado`) diz `ausente`, `reiniciando`,
   * `aguardando_turnos` ou `erro`.
   *
   * Irmão do `opencode_sem_chave`, e separado dele pelo gesto: lá o ADMIN
   * cadastra a chave; aqui ela já existe e está a caminho (a API a empurra
   * depois do olá e na reconciliação do pulso) — espera-se, ou, com `erro`,
   * olha-se a máquina. A capacidade mede só a INSTALAÇÃO (decisão da
   * orquestradora, costura S2): a chave chega DEPOIS do olá, e uma capacidade
   * que dependesse dela nasceria reprovada em todo boot.
   */
  opencodeSemChaveNaMaquina: 'opencode_sem_chave_na_maquina',
  /**
   * `RECURSO_OPENCODE` desligado na API (D32). É o estado de nascença em
   * produção, até as máquinas estarem instaladas.
   */
  recursoOpencodeDesligado: 'recurso_opencode_desligado',
  /**
   * O modelo escolhido não aceita ferramentas (`tools`) no catálogo do
   * OpenRouter. Uma sessão de código sem ferramenta não lê, não edita e não
   * roda nada — nasceria inútil, e cobrando.
   */
  modeloSemFerramentas: 'modelo_sem_ferramentas',
  /*
    Não existe `imagem_sem_visao`, e é decisão (D23 revista, 2026-09-24): o
    turno com imagem vai sozinho ao modelo de visão da config
    (`modeloDoTurnoComImagem`), então não há imagem para recusar. Medido: o
    DeepSeek V4.1 Flash diz `image: true` no catálogo e, pelo OpenRouter, não
    vê a imagem — recusar pelo catálogo deixaria passar justamente esse caso.
  */
  /**
   * O esforço (`variant`) não existe para aquele modelo no catálogo. Os
   * variants são POR MODELO (medido na Fase 0: o DeepSeek flash não tem
   * `medium`), então o esforço que valia no modelo anterior pode não valer no
   * próximo.
   */
  esforcoIndisponivelNoModelo: 'esforco_indisponivel_no_modelo',
  /**
   * O catálogo do OpenRouter não veio (rede, tempo esgotado ou resposta fora
   * da forma) e não havia cópia recente. Código próprio porque o gesto é
   * outro: não há nada errado na máquina nem na chave — é esperar e tentar de
   * novo. Nunca vira "lista vazia": a tela diria "nenhum modelo" e mentiria.
   */
  openrouterIndisponivel: 'openrouter_indisponivel',
  /**
   * O irmão do `openrouter_indisponivel` para o NanoGPT (F2 da revisão de
   * out/2026): o catálogo do NanoGPT não veio. Código próprio porque a frase
   * nomeia QUEM não respondeu — "o OpenRouter não respondeu" com o NanoGPT
   * ativo mandaria o dono olhar o provedor errado.
   */
  nanogptIndisponivel: 'nanogpt_indisponivel',
  /**
   * O modelo é de um provedor que não é o ATIVO (F2, D11): o `serve` só tem a
   * chave do provedor ativo no ambiente, e o modelo do outro não responderia.
   * Recusado na criação, na troca de modelo e no salvar da configuração.
   */
  modeloDeOutroProvedor: 'modelo_de_outro_provedor',
  /*
    O toolkit (F3 da revisão de out/2026, ADR-0026). As frases moram em
    `FRASES_DOS_ERROS_DO_TOOLKIT` (`toolkit/rest.ts`), ao lado do contrato.
  */
  /** Nome de rotina, chave de alvo ou termo de verbete que já existe (409). */
  toolkitNomeEmUso: 'toolkit_nome_em_uso',
  /** A rotina, a extensão ou o verbete cita um alvo que o catálogo não tem. */
  toolkitAlvoDesconhecido: 'toolkit_alvo_desconhecido',
  /** A proposta já tem decisão: decidir de novo seria reescrever (409). */
  toolkitPropostaJaDecidida: 'toolkit_proposta_ja_decidida',
  /** Um arquivo ou o pacote passou do teto (`LIMITES_TOOLKIT`): recusa, nunca trunca. */
  toolkitPacoteGrandeDemais: 'toolkit_pacote_grande_demais',
  /** A `entrada` do manifesto não está entre os arquivos do pacote. */
  toolkitEntradaForaDoPacote: 'toolkit_entrada_fora_do_pacote',
  /** Dois arquivos do pacote com o mesmo caminho: qual vale dependeria da ordem. */
  toolkitCaminhoRepetido: 'toolkit_caminho_repetido',
  /** O projeto que a rotina declara não é um que esta conta enxerga. */
  toolkitProjetoForaDoAlcance: 'toolkit_projeto_fora_do_alcance',
  /** Só a sessão MESTRE propõe pelo canal (D19). */
  toolkitSoMestrePropoe: 'toolkit_so_mestre_propoe',
  /** A sessão que chama não tem criador vivo: não há escopo para emprestar. */
  toolkitSessaoSemCriador: 'toolkit_sessao_sem_criador',
  /** A fase pedida não existe no manifesto da vigente. */
  toolkitFaseDesconhecida: 'toolkit_fase_desconhecida',
  /** O hash que o daemon conferiu não é o da versão: algo mudou no caminho. */
  toolkitHashDivergente: 'toolkit_hash_divergente',
  /** A rotina não declara rodar nesta máquina (`onde`, D23). */
  toolkitMaquinaForaDoOnde: 'toolkit_maquina_fora_do_onde',
  /** Validação sem sessão é só da fase `sonda` (F3.5): a das outras fases vem de dentro de uma sessão. */
  toolkitSoASondaSemSessao: 'toolkit_so_a_sonda_sem_sessao',
  /** A sonda registrada não é da versão vigente — a que o `preparar_sonda` entregaria agora. */
  toolkitSondaDeOutraVersao: 'toolkit_sonda_de_outra_versao',
  /** A rotina ou o verbete está arquivado: só leitura. */
  toolkitArquivada: 'toolkit_arquivada',
  /** As recusas de `podeRodar` (estado no alvo × classe da fase, D30, D34). */
  rotinaSemVersaoAprovada: 'rotina_sem_versao_aprovada',
  rotinaQuebradaNoAlvo: 'rotina_quebrada_no_alvo',
  rotinaNaoSeAplicaAoAlvo: 'rotina_nao_se_aplica_ao_alvo',
  soLeituraNesteEstado: 'so_leitura_neste_estado',
  atoExternoForaDaFase1: 'ato_externo_fora_da_fase_1',
} as const;

export type CodigoErro = (typeof CODIGOS_ERRO)[keyof typeof CODIGOS_ERRO];

/**
 * As frases pt-BR dos códigos do OpenCode, para o painel mostrar.
 *
 * Moram aqui, ao lado dos códigos, porque três telas as usam (criar sessão,
 * criar mestre, trocar de motor) e a regra do front é decidir a mensagem pelo
 * CÓDIGO, nunca pelo texto cru do servidor. Uma cópia por tela divergiria na
 * primeira revisão de texto.
 *
 * `Record` sobre a lista fechada: código novo do OpenCode sem frase não compila.
 */
export const FRASES_DOS_ERROS_DO_OPENCODE: Record<
  | typeof CODIGOS_ERRO.opencodeSoMestreNaVps
  | typeof CODIGOS_ERRO.confirmacaoOpencodeObrigatoria
  | typeof CODIGOS_ERRO.modoFixoDaMestreOpencode
  | typeof CODIGOS_ERRO.modeloDaMestreVemDaConfig
  | typeof CODIGOS_ERRO.opencodeIndisponivel
  | typeof CODIGOS_ERRO.opencodeSemChave
  | typeof CODIGOS_ERRO.opencodeSemChaveNaMaquina
  | typeof CODIGOS_ERRO.recursoOpencodeDesligado
  | typeof CODIGOS_ERRO.modeloSemFerramentas
  | typeof CODIGOS_ERRO.esforcoIndisponivelNoModelo
  | typeof CODIGOS_ERRO.openrouterIndisponivel
  | typeof CODIGOS_ERRO.nanogptIndisponivel
  | typeof CODIGOS_ERRO.modeloDeOutroProvedor,
  string
> = {
  opencode_so_mestre_na_vps: 'Na VPS o OpenCode só roda a sessão mestre. Crie a sessão num PC.',
  // Genéricas desde a F2: o provedor ativo pode ser o NanoGPT (assinatura) ou o OpenRouter (crédito).
  confirmacao_opencode_obrigatoria:
    'Confirme que a conversa sai da rede de casa e que a sessão usa a assinatura ou o crédito do provedor de modelos.',
  modo_fixo_da_mestre_opencode: 'A mestre do OpenCode tem modo fixo: ela não troca de modo.',
  modelo_da_mestre_vem_da_config:
    'O modelo da mestre do OpenCode é escolhido na tela de Configurações, não aqui.',
  opencode_indisponivel: 'O OpenCode não está disponível nesta máquina agora.',
  opencode_sem_chave: 'Falta a chave do provedor de modelos. Um ADMIN cadastra na tela de Configurações.',
  opencode_sem_chave_na_maquina:
    'A chave do provedor de modelos ainda não foi aplicada nesta máquina. Espere alguns segundos e tente de novo.',
  recurso_opencode_desligado: 'O OpenCode ainda não foi ligado neste painel.',
  modelo_sem_ferramentas: 'Este modelo não aceita ferramentas, e sem elas a sessão não consegue trabalhar.',
  esforco_indisponivel_no_modelo: 'Este nível de esforço não existe para o modelo escolhido.',
  openrouter_indisponivel: 'O catálogo do OpenRouter não respondeu agora. Tente de novo em instantes.',
  nanogpt_indisponivel: 'O catálogo do NanoGPT não respondeu agora. Tente de novo em instantes.',
  modelo_de_outro_provedor:
    'Este modelo é de um provedor que não está ativo. Escolha um modelo do provedor ativo.',
};
